import { TextField, Autocomplete, ListItem } from '@mui/material';
import { useFormContext, Controller } from 'react-hook-form';
import { convertToDisplayName } from '../helpers';
import { FormSelectOption } from '../types';
import { GenericFormComponentProps } from '../types';
import formInputStyles from '../shared.module.scss';
import { useEffect, useState } from 'react';
import { useFormSectionContext } from '../FormSection/FormSection';
import { useFormState } from 'hooks/useFormState';

interface FormAutocompleteProps extends GenericFormComponentProps {
    options: FormSelectOption[];
    disableClearable?: boolean;
    onSearch?: (e: string) => void;
    isLoading?: boolean;
    onBlur?: () => void;
}

/**
 * component used for auto-completing fields associated with a list of options to choose from
 * @param {string} field - field to track with react-hook-form
 * @param {string} label - optional label override
 * @param {FormSelectOption[]} options - list of options to pick from
 * @param {boolean} disabled - is this component readonly?
 * @param {string} className - for styling
 * @param {boolean} disableClearable - optional param to disable the clear button on autocomplete input. False by default so that clear is enabled.
 * @param {function} onSearch - optional function invoked in the text field's onChange
 * @param {function} isLoading - optional boolean for autocompletes that search for options
 * @param {function} onBlur - optional function invoked when component is blurred
 * @returns FormAutocomplete component
 */
export function FormAutocomplete({
    field,
    label,
    options,
    disabled,
    className,
    disableClearable = false,
    onSearch,
    isLoading,
    onBlur,
}: FormAutocompleteProps) {
    const { control, getFieldState } = useFormContext();
    const { setFormSectionError } = useFormState();
    const { formSection } = useFormSectionContext();

    const watchedField = getFieldState(field);

    useEffect(() => {
        setFormSectionError({
            section: formSection,
            field: { name: field, error: !!watchedField.error },
        });
    }, [field, formSection, setFormSectionError, watchedField.error]);

    const [inputValue, setInputValue] = useState('');
    const [selectedValue, setSelectedValue] = useState<FormSelectOption | null>(
        { value: '' }
    );

    return (
        <Controller
            name={field}
            control={control}
            render={({ field: fieldProps, fieldState: { error } }) => {
                label = label || convertToDisplayName(field);
                const id = `form-select-${field}`;
                return (
                    <Autocomplete
                        id={id}
                        options={options}
                        autoSelect={false}
                        disabled={disabled}
                        loading={isLoading}
                        onChange={(_, data) => {
                            setSelectedValue(data);
                            fieldProps.onChange(data?.value);
                        }}
                        onInputChange={(_, data) => {
                            if (onSearch) {
                                onSearch(data);
                            }

                            setInputValue(data);
                        }}
                        inputValue={inputValue}
                        className={`${formInputStyles.formInput} ${className}`}
                        value={selectedValue}
                        clearOnBlur={false}
                        onBlur={onBlur}
                        getOptionLabel={(option) => {
                            return (
                                option?.name ??
                                option?.value ??
                                ''
                            ).toString();
                        }}
                        renderOption={(props, option) => (
                            <ListItem
                                {...props}
                                key={option?.value}
                                value={option?.value}
                            >
                                {option?.name ?? option?.value ?? ''}
                            </ListItem>
                        )}
                        isOptionEqualToValue={(option, value) => {
                            return option?.value === (value?.value ?? value);
                        }}
                        renderInput={(params) => {
                            return (
                                <TextField
                                    {...params}
                                    label={label}
                                    fullWidth
                                    variant="standard"
                                    inputProps={{
                                        ...params.inputProps,
                                        autoComplete: 'disabled',
                                        'data-testid': 'form-autocomplete',
                                    }}
                                    helperText={error?.message}
                                />
                            );
                        }}
                        getOptionDisabled={(option) => !!option?.disabled}
                        color={error ? 'error' : 'primary'}
                        disableClearable={disableClearable}
                    />
                );
            }}
        />
    );
}
